import type { DocumentData, DocumentReference, Query, QuerySnapshot, Unsubscribe } from 'firebase/firestore'
import { addDoc, deleteDoc, doc, endAt, getDocs, increment, onSnapshot, orderBy, query, serverTimestamp, startAt, updateDoc, writeBatch } from 'firebase/firestore'
import {
  collectionMenuCategories,
} from '../firestoreWrappers/firestoreWrappers'
import { errorDefault, errorEmail, savedDefault } from '../helpers/snackbar'
import { isManagerOrDeliver, isOwner } from '../helpers/roles'
import { firestore } from '../firebaseCore'
import { getIdFromRef } from '../helpers/getIdFromRef'
import { mapNotSentNotificationSuppliers, mapSuppliers, mapSuppliersHomeDelivery, mapSuppliersSubsidy, SupplierModel } from '../models/SupplierModel'
import { mapOrders, Order } from './orders'
import { useRouteStore } from './routes'
import {
  collectionSupplier,
  getNotSentNotificationSuppliersByCompany,
  getNotSentNotificationSuppliersByCompany2,
  getNotSentNotificationSuppliersByCompanyCity,
  getNotSentNotificationSuppliersByCompanyCity2,
  getSuppliersByCompany,
  getSuppliersByCompanyCity,
  getSuppliersByCompanyRoute,
  getSuppliersByCompanyRouteCity,
  getSuppliersSearcheCity,
  getSuppliersSearch as getSuppliersSearchQuery,
  getSuppliersSubsidyByCompanyRoute,
  getSuppliiersByCode,
} from '~/firestoreWrappers/suppliers'
import { currentOrdersRef } from '~/firestoreWrappers/orders'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import type { Geopoint } from 'geofire-common'
import { distanceBetween } from 'geofire-common'
import type { RouteModel } from '~/models/RouteModel'
import type { DeliverModel } from '~/models/DeliverModel'
import { useSharedStore } from './shared'
import { useSnackbarStore } from './snackbar'

function findUniqueCode(success: any) {
  const code = Math.round(100000 + Math.random() * 900000)
  const onSuccess = (arg0: DocumentData) => {
    if (arg0.docs.length)
      findUniqueCode(success)
    else
      success(code)
  }

  getDocs(getSuppliiersByCode(code))
    .then(onSuccess)
}

export const useSuppliersStore = defineStore('suppliers', () => {
  const suppliersPerRoute: Ref<Map<string, SupplierModel[]>> = ref(new Map())
  const suppliers: Ref<SupplierModel[] | null> = ref(null)
  const suppliersSearch: Ref<SupplierModel[] | null> = ref(null)
  const suppliersSubsidy: Ref<SupplierModel[] | null> = ref(null)
  const notSentNotificationSuppliers: Ref<SupplierModel[] | null> = ref(null)
  const notSentNotificationSuppliers2: Ref<SupplierModel[] | null> = ref(null)
  const suppliersAdditional: Ref<SupplierModel[] | null> = ref(null)
  const suppliersInArea: Ref<SupplierModel[] | null> = ref(null)
  const fetchLoading: Ref<boolean> = ref(false)
  const fetchError: Ref<unknown | null> = ref(null)
  const fetchAdditionalLoading: Ref<boolean> = ref(false)
  const fetchAdditionalError: Ref<unknown | null> = ref(null)
  const createLoading: Ref<boolean> = ref(false)
  const createError: Ref<unknown | null> = ref(null)
  const fetchLoadingSubsidy: Ref<boolean> = ref(false)
  const fetchNotSentNotificationError: Ref<unknown | null> = ref(null)
  const fetchNotSentNotificationLoading: Ref<boolean> = ref(true)
  const delivers: Ref<DeliverModel[] | null> = ref(null)

  const lastSupplierDoc: Ref<DocumentData | null> = ref(null)
  const unsubscribe: Ref<Unsubscribe | null> = ref(null)
  const unsubscribe2: Ref<Unsubscribe | null> = ref(null)

  function resetState() {
    if (unsubscribe.value) {
      unsubscribe.value()
    }

    if (unsubscribe2.value) {
      unsubscribe2.value()
    }

    unsubscribe.value = null
    unsubscribe2.value = null

    suppliersPerRoute.value = new Map()
    suppliers.value = null
    suppliersSearch.value = null
    suppliersSubsidy.value = null
    suppliersAdditional.value = null
    suppliersInArea.value = null
    notSentNotificationSuppliers.value = null
    notSentNotificationSuppliers2.value = null
    fetchLoadingSubsidy.value = false
    fetchLoading.value = false
    fetchError.value = null
    fetchAdditionalLoading.value = false
    fetchAdditionalError.value = null
    lastSupplierDoc.value = null
    createLoading.value = false
    createError.value = null
    fetchNotSentNotificationError.value = null
    fetchNotSentNotificationLoading.value = true
    delivers.value = null
  }

  function getSuppliers(routeRef: DocumentReference, userData: any, user: any) {
    if (!routeRef || !userData?.company)
      return

    const {
      company,
      'profile': { cities },
    } = userData

    fetchLoading.value = true
    fetchError.value = null

    const onSuccess = (arg0: DocumentData) => {
      fetchLoading.value = false
      fetchError.value = null
      suppliers.value = arg0.docs.map(mapSuppliers(routeRef))
    }
    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchAdditionalLoading.value = false
      fetchAdditionalError.value = error
      fetchError.value = error
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    if (isManagerOrDeliver(user.displayName)) {
      getDocs(getSuppliersByCompanyRouteCity(
        company,
        routeRef,
        cities,
      ))
        .then(onSuccess)
        .catch(onError)
    }
    else if (isOwner(user.displayName)) {
      getDocs(getSuppliersByCompanyRoute(
        company,
        routeRef,
      ))
        .then(onSuccess)
        .catch(onError)
    }
  }

  async function getSuppliersInRoutesWithLimit(userData: any, user: any, routes: DocumentReference[]) {
    if (!routes.length || !userData?.company)
      return

    const {
      company,
      profile: { cities },
    } = userData

    fetchLoading.value = true
    fetchError.value = null

    const notSavedRoutes = routes.filter((refTemp: DocumentReference) => !suppliersPerRoute.value.has(refTemp.id))

    const fetchSuppliersBatch = async (routeRef: DocumentReference, lastDoc: DocumentData | null = null) => {
      let routesQuery: Query<DocumentData, DocumentData> | null = null

      if (isManagerOrDeliver(user.displayName)) {
        routesQuery = getSuppliersByCompanyRouteCity(
          company,
          routeRef,
          cities,
        )
      }
      else if (isOwner(user.displayName)) {
        routesQuery = getSuppliersByCompanyRoute(
          company,
          routeRef,
        )
      }

      if (!routesQuery)
        return []

      if (lastDoc) {
        routesQuery = query(routesQuery, startAt(lastDoc))
      }

      const response = await getDocs(routesQuery) as QuerySnapshot<DocumentData, DocumentData>
      const data = response.docs.map(mapSuppliers(routeRef))

      if (suppliersPerRoute.value.has(routeRef.id)) {
        const existingData = suppliersPerRoute.value.get(routeRef.id) ?? []
        suppliersPerRoute.value.set(routeRef.id, Array.isArray(existingData) ? [...existingData, ...data] : [...data])
      }
      else {
        suppliersPerRoute.value.set(routeRef.id, data)
      }

      if (response.docs.length === 25) {
        await fetchSuppliersBatch(routeRef, response.docs[response.docs.length - 1])
      }
    }

    const promises = notSavedRoutes.map((routeRef: DocumentReference) =>
      fetchSuppliersBatch(routeRef),
    )

    await Promise.all(promises)

    const data = Array.from(suppliersPerRoute.value.values()).flat()
    suppliers.value = data
    fetchLoading.value = false
    fetchError.value = null
  }

  async function getSuppliersWithLimit(userData: any, user: any, isActive: boolean = true, limit: number = 10, reset: boolean = false) {
    if (!userData?.company)
      return

    const {
      company,
      'profile': { cities },
    } = userData

    fetchLoading.value = true
    fetchError.value = null

    const lastDoc = reset ? null : lastSupplierDoc.value
    let suppliersTemp: SupplierModel[] = reset ? [] : (suppliers.value || [])

    const getSuppliersQuery = () => {
      if (isManagerOrDeliver(user.displayName)) {
        return getSuppliersByCompanyCity(
          company,
          cities,
          isActive,
          limit,
          lastDoc,
        )
      }
      else if (isOwner(user.displayName)) {
        return getSuppliersByCompany(
          company,
          isActive,
          limit,
          lastDoc,
        )
      }
      return null
    }

    try {
      const suppliersQuery = getSuppliersQuery()
      if (!suppliersQuery)
        return
      const snapshot = await getDocs(suppliersQuery)
      const batch = snapshot.docs.map((arg: any) => new SupplierModel(arg.data(), arg.ref))
      suppliersTemp = suppliersTemp.concat(batch)
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : lastDoc

      lastSupplierDoc.value = newLastDoc
      suppliers.value = suppliersTemp
      fetchLoading.value = false
      fetchError.value = null
    }
    catch (error) {
      console.error(error)
      fetchError.value = error
      fetchLoading.value = false
      useSnackbarStore().showSnackbar(errorDefault(error))
    }
  }

  function changeActivity(data: SupplierModel[], isActive: boolean, userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const batch = writeBatch(firestore)

    data.forEach((item: SupplierModel) => {
      if (!item.reference)
        return

      batch.update(
        item.reference,
        {
          isActive,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
    })

    const onSuccess = () => {
      const getId = (item: SupplierModel) => item.reference?.id
      const dataRefs = data.map(getId)

      const filterAndUpdateItems = (item: SupplierModel) => {
        if (dataRefs.includes(item.reference?.id)) {
          const tmpItem = item
          tmpItem.isActive = isActive
          return tmpItem
        }
        return item
      }
      fetchLoading.value = false
      fetchError.value = null
      suppliers.value = suppliers.value?.map(filterAndUpdateItems) || []
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchAdditionalLoading.value = false
      fetchAdditionalError.value = error
      fetchError.value = error
    }

    batch.commit()
      .then(onSuccess)
      .catch(onError)
  }

  function getSuppliersSearch(search: string[], userData: any, user: any) {
    if (!userData?.company)
      return

    const {
      company,
      'profile': { cities },
    } = userData

    fetchLoading.value = true
    fetchError.value = null

    const onSuccess = (arg0: DocumentData) => {
      const data = arg0.docs.map(mapSuppliersHomeDelivery)

      if (isManagerOrDeliver(user.displayName)) {
        suppliersSearch.value = data.filter((item: SupplierModel) => cities.includes(item.place?.city))
      }
      else if (isOwner(user.displayName)) {
        suppliersSearch.value = arg0.docs.map(mapSuppliersHomeDelivery)
      }
      fetchError.value = null
      fetchLoading.value = false
    }
    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchAdditionalLoading.value = false
      fetchAdditionalError.value = error
      fetchError.value = error
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }
    if (isManagerOrDeliver(user.displayName)) {
      getDocs(getSuppliersSearcheCity(
        company,
        cities,
        search,
      ))
        .then(onSuccess)
        .catch(onError)
    }
    else if (isOwner(user.displayName)) {
      getDocs(getSuppliersSearchQuery(
        company,
        search,
      ))
        .then(onSuccess)
        .catch(onError)
    }
  }

  function resetSearchSuppliers() {
    suppliersSearch.value = null
  }

  function createSupplier(supplier: SupplierModel, userData: any) {
    createError.value = null
    createLoading.value = true

    // @ts-expect-error
    supplier.menu = doc(
      collectionMenuCategories,
      getIdFromRef(supplier.company),
    )

    findUniqueCode((uniqueCode: any) => {
      let createdSupplierRef: any

      const onSuccessCreateSupplier = (supplierRef: any) => {
        createdSupplierRef = supplierRef
        supplier.reference = supplierRef
        suppliers.value = [supplier, ...(suppliers.value || [])]
        createLoading.value = false
        createError.value = null
        useSnackbarStore().showSnackbar(savedDefault,
        )
      }

      const onErrorCreateSupplier = (error: unknown) => {
        const deleteData = () => {
          createLoading.value = false
          createError.value = error
          useSnackbarStore().showSnackbar(errorEmail(error),
          )
        }
        deleteDoc(createdSupplierRef).then(deleteData)
      }

      supplier.code = String(uniqueCode)
      supplier.tags = [
        ...supplier.tags || [],
        String(uniqueCode),
      ]

      addDoc(
        collectionSupplier,
        {
          ...supplier.toMap(),

          createdByUser: userData.reference,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
        .then(onSuccessCreateSupplier)
        .catch(onErrorCreateSupplier)
    })
  }

  function editSupplier(supplier: SupplierModel, userData: any) {
    if (!supplier.reference) {
      return
    }

    createError.value = null
    createLoading.value = true

    const newSuppliers = [...suppliers.value || []]
    const supplierIndex = newSuppliers.findIndex(value => getIdFromRef(value.reference) == getIdFromRef(supplier.reference))

    const supplierCopy = supplier
    supplierCopy.lastUpdateByUser = userData.reference
    supplierCopy.lastUpdateTime = serverTimestamp()

    updateDoc(
      supplier.reference,
      supplierCopy.toMap(),
    )
      .then(() => {
        newSuppliers[supplierIndex] = supplierCopy
        suppliers.value = newSuppliers
        createError.value = null
        createLoading.value = false

        useSnackbarStore().showSnackbar(savedDefault,
        )
      })
      .catch((error) => {
        createError.value = error
        createLoading.value = false
        useSnackbarStore().showSnackbar(errorEmail(error),
        )
      })
  }

  function updateSuppliersArray(suppliersArray: SupplierModel[], value: boolean, userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const batch = writeBatch(firestore)

    suppliersArray.forEach((item: any) => {
      batch.update(
        item.reference,
        {
          isBuyOnline: value,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )
    })

    const onSuccess = () => {
      const getId = (item: SupplierModel) => item.reference?.id
      const dataRefs = suppliersArray.map(getId)

      const filterAndUpdateItems = (item: SupplierModel) => {
        if (dataRefs.includes(item.reference?.id)) {
          const tmpItem = item
          tmpItem.isBuyOnline = value
          return tmpItem
        }
        return item
      }
      fetchLoading.value = false
      fetchError.value = null
      suppliers.value = suppliers.value?.map(filterAndUpdateItems) || []
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchLoading.value = false
      fetchAdditionalLoading.value = false
      fetchAdditionalError.value = error
      fetchError.value = error
    }

    batch.commit()
      .then(onSuccess)
      .catch(onError)
  }

  function getSuppliersSubsidy(userData: any, user: any) {
    if (!user.displayName)
      return

    fetchLoadingSubsidy.value = true
    fetchError.value = null

    const onSuccess = (arg0: DocumentData) => {
      suppliersSubsidy.value = arg0.docs.map(mapSuppliersSubsidy)
      fetchLoadingSubsidy.value = false
      fetchError.value = null
    }
    const onError = (error: unknown) => {
      console.error(error)
      fetchLoadingSubsidy.value = false
      fetchError.value = error
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    getDocs(getSuppliersSubsidyByCompanyRoute(userData.company))
      .then(onSuccess)
      .catch(onError)
  }

  function getNotSentNotificationSuppliers(userData: any, user: any, routes: RouteModel[]) {
    if (unsubscribe.value) {
      unsubscribe.value()
      unsubscribe.value = null
    }
    if (unsubscribe2.value) {
      unsubscribe2.value()
      unsubscribe2.value = null
    }

    fetchNotSentNotificationError.value = null
    fetchNotSentNotificationLoading.value = true

    const onSuccess = (arg0: DocumentData) => {
      notSentNotificationSuppliers.value = arg0.docs.map(mapNotSentNotificationSuppliers(routes))
      fetchNotSentNotificationError.value = null
      fetchNotSentNotificationLoading.value = false
    }
    const onSuccess2 = (arg0: DocumentData) => {
      notSentNotificationSuppliers2.value = arg0.docs.map(mapNotSentNotificationSuppliers(routes))
      fetchNotSentNotificationError.value = null
      fetchNotSentNotificationLoading.value = false
    }
    const onError = (error: unknown) => {
      console.error(error)
      fetchNotSentNotificationError.value = error
      fetchNotSentNotificationLoading.value = false
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    if (isManagerOrDeliver(user.displayName) || isOwner(user.displayName)) {
      const query1 = isOwner(user.displayName)
        ? getNotSentNotificationSuppliersByCompany(userData.company)
        : getNotSentNotificationSuppliersByCompanyCity(
            userData.company,
            userData.profile.cities,
          )

      const unsubscribeNew = onSnapshot(
        query1,
        onSuccess,
        onError,
      )

      unsubscribe.value = unsubscribeNew

      const query2 = isOwner(user.displayName)
        ? getNotSentNotificationSuppliersByCompany2(userData.company)
        : getNotSentNotificationSuppliersByCompanyCity2(
            userData.company,
            userData.cities,
          )

      const unsubscribeNew2 = onSnapshot(
        query2,
        onSuccess2,
        onError,
      )

      unsubscribe2.value = unsubscribeNew2
    }
    else {
      onError('Wrong site')
    }
  }

  function setDelivers(deliversArray: DeliverModel[]) {
    delivers.value = [...deliversArray]
  }

  function getAdditionalSuppliers(routeRef: DocumentReference | null, userData: any, user: any) {
    if (!routeRef)
      return

    fetchAdditionalLoading.value = true
    fetchAdditionalError.value = null

    const onSuccess = (arg0: DocumentData) => {
      suppliersAdditional.value = arg0.docs.map(mapSuppliers(routeRef))
      fetchAdditionalError.value = null
      fetchAdditionalLoading.value = false
    }
    const onError = (error: unknown) => {
      console.error(error)
      fetchAdditionalLoading.value = false
      fetchAdditionalError.value = error
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
    }

    if (isManagerOrDeliver(user.displayName)) {
      getDocs(getSuppliersByCompanyRouteCity(
        userData.company,
        routeRef,
        userData.profile.cities,
      ))
        .then(onSuccess)
        .catch(onError)
    }
    else if (isOwner(user.displayName)) {
      getDocs(getSuppliersByCompanyRoute(
        userData.company,
        routeRef,
      ))
        .then(onSuccess)
        .catch(onError)
    }
  }

  function editSupplierSupplierView(supplier: SupplierModel, userData: any) {
    if (!supplier.reference)
      return

    createError.value = null
    createLoading.value = true

    const newSuppliers = [...suppliersSearch.value || []]
    const supplierIndex = newSuppliers.findIndex(value => getIdFromRef(value.reference) == getIdFromRef(supplier.reference))

    const supplierCopy = supplier
    supplierCopy.lastUpdateByUser = userData.reference
    supplierCopy.lastUpdateTime = serverTimestamp()

    updateDoc(
      supplier.reference,
      supplierCopy.toMap(),
    )
      .then(() => {
        newSuppliers[supplierIndex] = supplierCopy
        suppliersSearch.value = newSuppliers
        createError.value = null
        createLoading.value = false

        useSnackbarStore().showSnackbar(savedDefault,
        )
      })
      .catch((error) => {
        createError.value = error
        createLoading.value = false

        useSnackbarStore().showSnackbar(errorEmail(error),
        )
      })
  }

  function updateOrder(supplier: DocumentReference, pending: any, approved: any, userData: any) {
    fetchError.value = null
    fetchLoading.value = true

    const onSuccess = () => {
      const updateOnlySelectedSupplier = (supplierData: any) => {
        if (supplierData.reference.id === supplier.id) {
          const tmpSupplier = supplierData
          tmpSupplier.pendingOrders += pending
          tmpSupplier.approvedOrders += approved
          return tmpSupplier
        }
        return supplierData
      }
      const mapped = suppliers.value?.map(updateOnlySelectedSupplier) || []
      suppliers.value = mapped
      fetchError.value = null
      fetchLoading.value = false
    }

    const onError = (error: unknown) => {
      console.error(error)
      fetchError.value = error
      fetchLoading.value = false
    }

    // @ts-expect-error
    const findSupplierData = supplierData => supplierData.reference.id === supplier.id

    const [currentSupplierData] = suppliers.value?.filter(findSupplierData) || []

    if (!currentSupplierData.reference)
      return

    const updatePendingOrders = updateDoc(
      currentSupplierData.reference,
      {
        pendingOrders: increment(pending),
        lastUpdateByUser: userData.reference,
        lastUpdateTime: serverTimestamp(),
      },
    )
    const updateCurrentSupplierData = updateDoc(
      currentSupplierData.reference,
      {
        approvedOrders: increment(approved),
        lastUpdateByUser: userData.reference,
        lastUpdateTime: serverTimestamp(),
      },
    )
    Promise.all([
      updatePendingOrders,
      updateCurrentSupplierData,
    ])
      .then(onSuccess)
      .catch(onError)
  }

  function changeSupplierRoute(supplier: SupplierModel | null, routeReference: DocumentReference | null, userData: any, routes: RouteModel[]) {
    if (!supplier || !supplier.reference || !routeReference || !userData?.company || !supplier.route)
      return

    useSharedStore().clearError()
    useSharedStore().setLoading(true,
    )

    const onError = (error: unknown) => {
      console.error(error)
      useSharedStore().setError(error,
      )
      useSnackbarStore().showSnackbar(errorDefault(error),
      )
      useSharedStore().setLoading(false,
      )
    }
    const onSuccessGetCurrentOrders = (arg0: DocumentData) => {
      const supplierOrders = arg0.docs.map(mapOrders) || []
      // @ts-expect-error
      const sumFunc = (sum: any, { completementStatus }) => (
        completementStatus === Order.completementStatus.waitingCustomerAccept
        || completementStatus === Order.completementStatus.waitingFirmAccept
          ? sum + 1
          : sum
      )

      const pendingOrdersCount = supplierOrders.reduce(
        sumFunc,
        0,
      )

      const byId = (route: RouteModel) => route.reference?.id === routeReference.id
      const [route] = routes.filter(byId)

      const onSuccess = () => {
        const updateSupplier = (item: SupplierModel) => supplier.reference?.id !== item.reference?.id

        const updatedRoutes = routes.map((routeData: any) => {
          if (routeData.reference.id === supplier.route?.id) {
            routeData.ordersCount -= supplierOrders.length || 0
            routeData.pendingOrders -= pendingOrdersCount || 0
          }
          else if (routeData.reference.id === route.reference?.id) {
            routeData.ordersCount += supplierOrders.length || 0
            routeData.pendingOrders += pendingOrdersCount || 0
          }
          return routeData
        })

        useRouteStore().updateRouteData(updatedRoutes)

        suppliers.value = suppliers.value?.filter(updateSupplier) || []
        fetchError.value = null
        fetchLoading.value = false
        useSharedStore().setLoading(false,
        )
      }

      if (!supplier.route)
        return

      const updateOldRoute = updateDoc(
        supplier.route,
        {
          ordersCount: increment(-supplierOrders.length || 0),
          pendingOrders: increment(-pendingOrdersCount || 0),

          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )

      if (!route.reference)
        return

      const updateNewRoute = updateDoc(
        route.reference,
        {
          ordersCount: increment(supplierOrders.length || 0),
          pendingOrders: increment(pendingOrdersCount || 0),

          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )

      // @ts-expect-error
      const supplierOrdersToUpdate = supplierOrders.map(order => updateDoc(
        order.reference,
        {
          route: route.reference,
          deliver: route.isSubDeliver
            ? route.subDeliver
            : route.deliver,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      ))

      if (!supplier.reference)
        return

      const updateSupplier = updateDoc(
        supplier.reference,
        {
          route: routeReference,
          deliverID: route.isSubDeliver
            ? route.subDeliver
            : route.deliver,
          lastUpdateByUser: userData.reference,
          lastUpdateTime: serverTimestamp(),
        },
      )

      Promise.all([
        updateOldRoute,
        updateNewRoute,
        updateSupplier,
        ...supplierOrdersToUpdate,
      ])
        .then(onSuccess)
        .catch(onError)
    }

    if (!supplier.company)
      return

    getDocs(currentOrdersRef(supplier.reference, supplier.company))
      .then(onSuccessGetCurrentOrders)
      .catch(onError)
  }

  function getSuppliersWithinBounds(bounds: any, center: Geopoint, radiusInM: number) {
    fetchLoading.value = true
    fetchError.value = null

    const onSuccess = (data: any) => {
      const matchingDocs: Array<SupplierModel> = []
      for (const snap of data) {
        for (const doc1 of snap.docs) {
          const lat = doc1.get('geoPoint')

          const distanceInKm = distanceBetween([lat._lat, lat._long], center)
          const distanceInM = distanceInKm * 1000
          if (distanceInM <= radiusInM)
            matchingDocs.push(mapSuppliersSubsidy(doc1))
        }
      }
      suppliersInArea.value = [...matchingDocs]
      suppliers.value = [...matchingDocs]
      fetchLoading.value = false
      fetchError.value = null
    }

    const onError = (error: unknown) => {
      fetchError.value = error
      fetchLoading.value = false
    }

    const promises: Promise<QuerySnapshot<DocumentData, DocumentData>> [] = []
    for (const b of bounds) {
      const q = query(
        collectionSupplier,
        orderBy('position.geohash'),
        startAt(b[0]),
        endAt(b[1]),
      )

      promises.push(getDocs(q))
    }

    Promise.all(promises)
      .then(onSuccess)
      .catch(onError)
  }

  function updateSuppliersAreaLocally(suppliersArray: SupplierModel[]) {
    fetchLoading.value = false
    fetchError.value = null
    suppliersInArea.value = suppliersArray
  }

  return {
    suppliersPerRoute,
    suppliersSubsidy,
    suppliers,
    suppliersInArea,
    suppliersAdditional,
    notSentNotificationSuppliers,
    notSentNotificationSuppliers2,
    suppliersSearch,
    fetchLoading,
    fetchError,
    createError,
    createLoading,
    fetchAdditionalLoading,
    fetchAdditionalError,
    fetchLoadingSubsidy,
    fetchNotSentNotificationError,
    fetchNotSentNotificationLoading,
    delivers,
    resetState,
    getSuppliers,
    getSuppliersInRoutesWithLimit,
    getSuppliersWithLimit,
    changeActivity,
    getSuppliersSearch,
    resetSearchSuppliers,
    createSupplier,
    editSupplier,
    updateSuppliersArray,
    getSuppliersSubsidy,
    getNotSentNotificationSuppliers,
    setDelivers,
    getAdditionalSuppliers,
    editSupplierSupplierView,
    updateOrder,
    changeSupplierRoute,
    getSuppliersWithinBounds,
    updateSuppliersAreaLocally,
  }
})
